// Token utility functions for WebSocket authentication

/**
 * Validates if a string is a valid JWT token format
 * @param {string} token - The token to validate
 * @returns {boolean} - True if token format is valid
 */
export const isValidJWT = (token) => {
  if (!token || typeof token !== 'string') {
    return false
  }
  
  // JWT tokens have 3 parts separated by dots
  const parts = token.split('.')
  if (parts.length !== 3) {
    return false
  }
  
  // Each part should be base64 encoded
  try {
    parts.forEach(part => {
      if (part.length === 0) {
        throw new Error('Empty part')
      }
      // Basic base64 validation
      atob(part.replace(/-/g, '+').replace(/_/g, '/'))
    })
    return true
  } catch (error) {
    return false
  }
}

/**
 * Decodes JWT payload without verification
 * @param {string} token - The JWT token
 * @returns {object|null} - Decoded payload or null if invalid
 */
export const decodeJWTPayload = (token) => {
  if (!isValidJWT(token)) {
    return null
  }
  
  try {
    const parts = token.split('.')
    const payload = parts[1]
    const decoded = atob(payload.replace(/-/g, '+').replace(/_/g, '/'))
    return JSON.parse(decoded)
  } catch (error) {
    console.error('Error decoding JWT payload:', error)
    return null
  }
}

/**
 * Checks if JWT token is expired
 * @param {string} token - The JWT token
 * @returns {boolean} - True if token is expired
 */
export const isTokenExpired = (token) => {
  const payload = decodeJWTPayload(token)
  if (!payload || !payload.exp) {
    return true
  }
  
  const currentTime = Math.floor(Date.now() / 1000)
  return payload.exp < currentTime
}

/**
 * Gets token expiration time
 * @param {string} token - The JWT token
 * @returns {Date|null} - Expiration date or null if invalid
 */
export const getTokenExpiration = (token) => {
  const payload = decodeJWTPayload(token)
  if (!payload || !payload.exp) {
    return null
  }
  
  return new Date(payload.exp * 1000)
}

/**
 * Gets user ID from token
 * @param {string} token - The JWT token
 * @returns {string|null} - User ID or null if not found
 */
export const getUserIdFromToken = (token) => {
  const payload = decodeJWTPayload(token)
  return payload?.sub || payload?.user_id || payload?.id || null
}

/**
 * Validates access token for WebSocket connection
 * @param {string} token - The access token
 * @returns {object} - Validation result
 */
export const validateAccessToken = (token) => {
  const result = {
    isValid: false,
    isExpired: false,
    userId: null,
    expiration: null,
    error: null
  }
  
  if (!token) {
    result.error = 'No token provided'
    return result
  }
  
  if (!isValidJWT(token)) {
    result.error = 'Invalid JWT format'
    return result
  }
  
  result.isValid = true
  result.userId = getUserIdFromToken(token)
  result.expiration = getTokenExpiration(token)
  result.isExpired = isTokenExpired(token)
  
  if (result.isExpired) {
    result.error = 'Token has expired'
  }
  
  return result
}
